// Renders the legacy carpenter `fa fa-{name}` markup as an inline FA Pro 6
// `<svg><use>` of the static Charge CDN assets, matching what the PHP `fa()`
// helper (Libs/ViewUtils.php) emits. This lets the last runtime emitters of
// raw FA4 classes drop the icomoon webfont (#61998 / #62040).
//
// The map is a strict subset of iFixit\Framework\Icons\FaIconLegacyMap, and
// faLegacyIcon.test.ts enforces that in both directions. FA4 names are frozen,
// so this doesn't drift; a name added here needs one there too, or PHP `fa()`
// falls through to its classic/solid passthrough and the same FA4 name renders
// two ways.
//
// Two of that map's behaviors are deliberately not mirrored, because nothing
// here needs them: it passes any unmapped `[a-z0-9-]` name through to
// classic/solid, and it resolves brands. An unmapped name returns null here and
// keeps the webfont, so adding a name on one side only makes the same FA4 name
// render two ways.

export type FaLegacyStyle = 'solid' | 'regular';

export const FA4_TO_FA6: Record<string, { style: FaLegacyStyle; name: string }> = {
   // FA4 `adjust` (the half-filled circle) was renamed in FA Pro 6.
   adjust: { style: 'solid', name: 'circle-half-stroke' },
   'angle-left': { style: 'solid', name: 'angle-left' },
   'angle-right': { style: 'solid', name: 'angle-right' },
   'arrow-left': { style: 'solid', name: 'arrow-left' },
   'arrow-right': { style: 'solid', name: 'arrow-right' },
   bold: { style: 'solid', name: 'bold' },
   'caret-down': { style: 'solid', name: 'caret-down' },
   'caret-left': { style: 'solid', name: 'caret-left' },
   'caret-right': { style: 'solid', name: 'caret-right' },
   certificate: { style: 'solid', name: 'certificate' },
   check: { style: 'solid', name: 'check' },
   'check-circle': { style: 'solid', name: 'circle-check' },
   circle: { style: 'solid', name: 'circle' },
   // FA4 `-o` outline variants map to Regular.
   'circle-o': { style: 'regular', name: 'circle' },
   // FA4 `cog` was renamed `gear` in FA Pro 6.
   cog: { style: 'solid', name: 'gear' },
   'exclamation-circle': { style: 'solid', name: 'circle-exclamation' },
   eye: { style: 'solid', name: 'eye' },
   'eye-slash': { style: 'solid', name: 'eye-slash' },
   'file-excel-o': { style: 'regular', name: 'file-excel' },
   'file-image-o': { style: 'regular', name: 'file-image' },
   'file-o': { style: 'regular', name: 'file' },
   'file-pdf-o': { style: 'regular', name: 'file-pdf' },
   'file-powerpoint-o': { style: 'regular', name: 'file-powerpoint' },
   'file-video-o': { style: 'regular', name: 'file-video' },
   'file-word-o': { style: 'regular', name: 'file-word' },
   gift: { style: 'solid', name: 'gift' },
   // FA4 `header` was renamed `heading` in FA Pro 6.
   header: { style: 'solid', name: 'heading' },
   // FA4 `image` was an alias for `picture-o`, the outline variant, so it
   // maps to Regular (not the Solid default) and matches `picture-o` below.
   image: { style: 'regular', name: 'image' },
   italic: { style: 'solid', name: 'italic' },
   link: { style: 'solid', name: 'link' },
   'list-ol': { style: 'solid', name: 'list-ol' },
   'list-ul': { style: 'solid', name: 'list-ul' },
   mobile: { style: 'solid', name: 'mobile' },
   'pencil-square-o': { style: 'regular', name: 'pen-to-square' },
   // FA4 `picture-o` was renamed `image` in FA Pro 6.
   'picture-o': { style: 'regular', name: 'image' },
   plus: { style: 'solid', name: 'plus' },
   'question-circle': { style: 'solid', name: 'circle-question' },
   'search-minus': { style: 'solid', name: 'magnifying-glass-minus' },
   'search-plus': { style: 'solid', name: 'magnifying-glass-plus' },
   'shopping-cart': { style: 'solid', name: 'cart-shopping' },
   'smile-o': { style: 'regular', name: 'face-smile' },
   sort: { style: 'solid', name: 'sort' },
   times: { style: 'solid', name: 'xmark' },
   trash: { style: 'solid', name: 'trash' },
   'trash-o': { style: 'regular', name: 'trash-can' },
   underline: { style: 'solid', name: 'underline' },
   // FA4 `video-camera` was renamed `video` in FA Pro 6.
   'video-camera': { style: 'solid', name: 'video' },
   // Not the YouTube brand mark: `classic/brands/youtube` isn't published on
   // Charge, and the only emitter (MediaLibrary's embed item) is
   // provider-agnostic — it renders the provider name as text beside this and
   // GuideEmbedObject accepts Vimeo too.
   'youtube-play': { style: 'solid', name: 'circle-play' },
};

// Same URL GuideURI::faIconHref() and the FaIcon React component build. The
// `?v=3` cache-buster keeps all three fetching the one cached asset.
function faIconHref(name: string, style: FaLegacyStyle): string {
   return `/_next/charge/icons/next/classic/${style}/${name}.svg?v=3#${name}`;
}

// Resolves a legacy FA4 name to the FA Pro 6 { style, name } the FaIcon React
// component takes, or null when the name isn't one carpenter JS emits.
export function resolveFaLegacyIcon(
   fa4Name: string
): { style: FaLegacyStyle; name: string } | null {
   return FA4_TO_FA6[fa4Name] ?? null;
}

// The inner `<svg><use>` markup for a legacy FA4 name, matching the SVG that
// PHP `fa()` nests inside its `<i class="fa fa-{name} fa-svg">` wrapper. Inject
// this into an existing `.fa fa-{name}` element after adding the `fa-svg` class
// (base.less keys the layout/fill and webfont-glyph suppression on `fa-svg`).
// Empty string for unmapped names.
export function faLegacyIconSvg(fa4Name: string): string {
   const resolved = FA4_TO_FA6[fa4Name];
   if (!resolved) {
      return '';
   }
   const href = faIconHref(resolved.name, resolved.style);
   return `<svg width="1em" height="1em" aria-hidden="true" focusable="false"><use href="${href}" /></svg>`;
}

// The full `<i class="fa fa-{name} fa-svg …">…</i>` markup PHP `fa()` emits, for
// string-render sites that build innerHTML (e.g. `el.set('html', …)`). Empty
// string for unmapped names.
export function faLegacyIconMarkup(fa4Name: string, extraClasses = ''): string {
   const svg = faLegacyIconSvg(fa4Name);
   if (!svg) {
      return '';
   }
   const classString = `fa fa-${fa4Name} fa-svg${extraClasses ? ` ${extraClasses}` : ''}`;
   return `<i class="${classString}">${svg}</i>`;
}
